// Used to lookup data from the api
// responses and process if necessary.
// Can be shared with other apps.

// @var home = a mortgage response from the api
if(typeof require !== 'undefined' && typeof _ === 'undefined'){
  var _ = require('lodash');
  var moment = require('moment');
  var finance = require('./Finance.js');
  var Helpers = require('./Helpers.js');
}

var LTV_PMI_ESTIMATE_MAP = {
  // termYears: {
  //   ltvPercentileCeil: pmiPercent
  // }
  '30': {
    '95': 0.0062,
    '90': 0.0044,
    '85': 0.0027,
    '80': 0.0027
  },
  '15': {
    '95': 0.0057,
    '90': 0.0039,
    '85': 0.0022,
    '80': 0.0022
  }
}

const GlobalDictionary = function(undefined){

  return {

    currentLoanWithMax: 'amount', //amount, date, rate

    loanStatus: 'current', //current, expired

    init: function(args){
      if(args){
        if(args.currentLoanWithMax && ['amount','date','rate'].indexOf(args.currentLoanWithMax) !== -1)
          this.currentLoanWithMax = args.currentLoanWithMax;
        if(args.loanStatus && ['current','expired'].indexOf(args.loanStatus) !== -1)
          this.loanStatus = args.loanStatus;
      }
    },

    availableEquity: function(home, factor){
      let homeValue = this.avm(home);
      let equity = this.equity(home);

      let downpaymentFactor = factor || 0.3;
      let estimatedDownpayment = homeValue * downpaymentFactor;
      let availableEquity = equity - estimatedDownpayment;

      return availableEquity > 0 ? availableEquity : 0;
    },

    avm: function(home){
      return this.cma(home) || this.avmWithoutCMA(home) || false;
    },

    avmDiffPercent: function(home){
      if(!home.avm || !home.avm.length)
        return null;

      let avms = [...home.avm].filter(avm => avm.type === 'avm');
      avms = Helpers.sortByDate(avms, 'updated');

      if(!avms.length || avms.length < 2) return null;

      return (avms[0].amount / avms[1].amount - 1) * 100;
    },

    avmWithoutCMA: function(home){
      if(!home.avm || !home.avm.length)
        return 0;

      let avms = home.avm.filter(loan => loan.rental !== true);
      avms = Helpers.sortByDate(avms, 'updated');

      return this._avmValueFromSource(avms, 'sitex')
        || this._avmValueFromSource(avms, 'realtytrac')
        || this._avmValueFromSource(avms, 'zillow')
        || 0;
    },

    _avmValueFromSource: function(avms, source){
      avms = avms.filter(avm => avm.source === source);
      if(avms.length) return avms[0].amount;
      return 0;
    },

    cma: function(home){
      if(_.isEmpty(home.cma))
        return 0;

      var avmWithoutCMA = this.avmWithoutCMA(home);

      if(!avmWithoutCMA)
        return home.cma.amount;
      else if(home.cma.amount === home.cma.diff)
        return avmWithoutCMA

      return avmWithoutCMA + parseInt(home.cma.diff,10);
    },

    cmaBuyersAgentPreferred: function(home){
      if(Helpers.isFalsey(home.answers.cmaBuyersAgentPreferred))
        return false;

      return true;
    },

    cmaBuyersAgentUnknown: function(home){
      return typeof home.answers.cmaBuyersAgentPreferred === 'undefined';
    },

    cmaDone: function(home){
      return Boolean(this.hasRealCMA(home) && !this.needsCMA(home));
    },

    appreciation: function(home){
      return this.avm(home) - home.soldPrice;
    },

    appreciationSentence: function(home){
      if(!home.soldPrice)
        return '';

      var appreciationPercent = Helpers.getAppreciationPercent(this.avm(home),home.soldPrice);

      if(appreciationPercent > 10)
        return "That's up " + appreciationPercent + "% from when you bought it. Nice!";
      else if(appreciationPercent > 0)
        return "That's up " + appreciationPercent + "% from when you bought it.";
      else if(appreciationPercent < 0)
        return "Down " + Math.abs(appreciationPercent) + "% from when you bought it.";
      else
        return '';
    },

    buyingPowerSecondHome: function(home){
      let downpaymentFactor = 0.3;
      let availableEquity = this.availableEquity(home, downpaymentFactor);
      let buyingPower = availableEquity * (1 / downpaymentFactor);

      return buyingPower >= 50000 ? buyingPower : 0;
    },

    buyingPowerTradeUp: function(home){
      let downpaymentFactor = 0.25;
      let homeValue = this.avm(home);
      let equity = this.equity(home);
      let tradeUpPower = equity * (1 / downpaymentFactor)

      return equity > 0 ? tradeUpPower : 0;
    },

    cashoutVal: function(home, args){
      var ltv = args && args.ltv ? args.ltv : .8;
      var cashout = ltv * this.avm(home) - this.outstandingPrincipalTotal(home);

      return cashout > 0 ? cashout : 0;
    },

    cashoutMinLTV: function(home){
      return this.cashoutLTVZeroBound(home) + 1;
    },

    cashoutLTVZeroBound: function(home){
      return Math.round((this.outstandingPrincipalTotal(home) / this.avm(home)) * 100);
    },

    cmaSourceAgent: function(home){
      if(!this.hasRealCMA(home) || !home.cma.sourceObject)
        return undefined;

      if(home.cma.sourceObject.type === 'mortgage')
        return undefined;

      return home.cma.sourceObject;
    },

    hasAutoCMA: function(home){
      return Boolean(home.cma && home.cma.systemGenerated);
    },

    hasRealCMA: function(home){
      return Boolean(!_.isEmpty(home.cma) && home.cma.type === 'cma' && !this.hasAutoCMA(home));
    },

    conventionalPmiMonthlyTotal: function(home){
      if(!home.loans || !home.loans.length)
        return 0;

      return _.chain(home.loans)
        .filter((loan) => {return loan.status === 'current' && loan.finance_type !== 'FHA'})
        .sum('pmi_monthly')
        .value();
    },

    fhaPmiMonthlyTotal: function(home){
      const maxLoan = this.maxLoan(home);

      if(!maxLoan || maxLoan.finance_type !== 'FHA') return 0;

      let pmiRate = this.pmiRateForFHA(maxLoan);

      if (pmiRate) {
        let pmiForFHA = maxLoan.amount * pmiRate;
        return (pmiForFHA / 12);
      }

      return 0;
    },

    estimatedPmiMonthlyTotal: function(home){
      const currentLoans = this.currentLoans(home);

      if (currentLoans.length !== 1) {
        return 0;
      }

      var loanAmount = currentLoans[0].amount;
      var originalLTV = (loanAmount / home.soldPrice) * 100;
      var termYears = this.termYears(home);

      var pmiRate = this.getLtvPmiEstimateRate(termYears, originalLTV);
      var pmiAnnual = pmiRate * loanAmount;

      return Math.round(pmiAnnual / 12);
    },

    getLtvPmiEstimateRate: function(loanTerm, originalLtv) {
      if (originalLtv <= 80) {
        return 0;
      }

      const ceilLTV = Math.ceil(originalLtv / 5) * 5;
      const maximumPmiEstimateLTV = Math.min(ceilLTV, 95);

      return _.get(
        LTV_PMI_ESTIMATE_MAP,
        [loanTerm, maximumPmiEstimateLTV],
      );
    },

    currentHelocs: function(home){
      return _.filter(this.loans(home), function(loan){
        return loan.status === 'current' && loan.loan_type === 'HELOC'
      });
    },

    currentLoans: function(home){
      return _.where(this.loans(home), {status: 'current'});
    },

    currentLoansNoHelocs: function(home){
      return _.filter(this.loans(home), function(loan){
        return loan.status === 'current' && loan.loan_type !== 'HELOC'
      });
    },

    customTermsEnabled: function(home) {
      return Boolean(home.agent[0].office.brokerage.offersCustomTerms);
    },

    //TODO if current loan is refi then can't use originalLoanAmount since this returns the current loan sum
    //either need to change originalLoanAmount or use expired loans to calc originalLoanAmount
    downPayment: function(home){
      return home.soldPrice - this.originalLoanAmountTotal(home);
    },

    eligibleRefiLoans: function(home){
      function nonRecentLoansFilter(loan){
        const fiveMonthsAgo = moment().subtract(5, 'months');
        return moment(loan.date).isBefore(fiveMonthsAgo);
      };

      return this.currentLoansNoHelocs(home)
        .filter(nonRecentLoansFilter);
    },

    equity: function(home){
      let homeValue = this.avm(home);
      let loans = this.currentLoansNoHelocs(home);
      let debt = this.outstandingPrincipalTotal({loans: loans});

      if(!loans.length) return homeValue;

      return homeValue - debt;
    },

    equityPercent: function(home){
      let homeValue = this.avm(home);
      let loans = this.currentLoansNoHelocs(home);
      let debt = this.outstandingPrincipalTotal({loans: loans});

      if(!loans.length) return 100;

      return (1 - (debt / homeValue)) * 100;
    },

    expiredLoans: function(home){
      return _.where(this.loans(home), {status: 'expired'});
    },

    extraPaymentSavingsTotal: function(home,args){
      return this.interestForFullLoanTotal(home) - this.extraPaymentObj(home, args).interest_paid;
    },

    extraPaymentObj: function(home, args){
      if(!args || !args.amt) throw('args.amt is required');
      return finance.getMaturityDate(this.originalLoanAmountTotal(home), this.periods(home), this.interestRate(home), new Date(this.originalLoanDate(home)), args.amt, false);
    },

    extraPaymentYears: function(home,args){
      var extraPayment = this.extraPaymentObj(home, args);
      var years = extraPayment.time_savings.years;
      var months = extraPayment.time_savings.months;

      if(months > 5)
        years ++;

      return years;
    },

    _hasEligibleRefiLoans: function(home){
      return Boolean(this.eligibleRefiLoans(home).length);
    },

    hasRecentLoan: function(home){
      return _.some(home.loans, function(loan){
        var fiveMonthsAgo = moment().subtract(5, 'months');
        return moment(loan.date).isAfter(fiveMonthsAgo);
      });
    },

    hasRecentClosedEndLoan: function(home){
      var currentLoansNoHelocs = this.currentLoansNoHelocs(home);
      return this.hasRecentLoan({loans: currentLoansNoHelocs});
    },

    hasUpToDateCMA: function(home){
      if(!this.hasRealCMA(home))
        return false;

      let requestDate = moment(home.hasRequestedCMA).subtract(24, 'hours');

      if(home.hasRequestedCMA && moment(home.cma.updated).isBefore(requestDate))
        return false;

      return true;
    },

    highestRateLoan: function(home){
      return _.max(_.where(home.loans, {'status': 'current'}), function(loan){
        return loan.rate || loan.rateEstimated;
      });
    },

    initialDIYPayment: function(home){
      var extraPayment = Math.round((this.monthlyPaymentTotal(home) * .15)/100) * 100;
      return extraPayment >= 100 ? extraPayment : 100;
    },

    interestForFullLoanTotal: function(home){
      if(home.loans && home.loans.length)
        return _.sum(_.where(home.loans, {'status': this.loanStatus}),'facts.totalInterest');
      return 0;
    },

    interestStatus: function(home){
      if(this.hasRecentClosedEndLoan(home))
        return 'recentLoans';
      else if(this.refiSavingsTotal(home, {rateType:'FIX15YR'}) < 0)
        return 'good';
      else
        return 'bad';
    },

    interestPaidTotal: function(home){
      if(home.loans && home.loans.length)
        return _.sum(_.where(home.loans, {'status': this.loanStatus}),'progress.interest_paid');
      return 0;
    },

    interestRate: function(home){
      var maxLoan = this.maxLoan(home);
      if(maxLoan)
        return maxLoan.rate || maxLoan.rateEstimated;

      return this.marketRateOnSoldDate(home);
    },

    interestSavings: function(home){
      var savings = 0;
      if(this.extraPaymentSavingsTotal(home) > 0)
        savings += this.extraPaymentSavingsTotal(home);
      if(this.refiSavingsTotal(home) > 0)
        savings += this.refiSavingsTotal(home);

      return savings;
    },

    isConsolidation: function(home){
      return home.loans.length > 1;
    },

    isFHA: function(home) {
      if(!home.loans || !home.loans.length)
        return false;

      return this.maxLoan(home).finance_type === 'FHA';
    },

    latestAvmIsCma: function(home){
      if(!home.avm || !home.avm.length)
        return false;

      let avms = [...home.avm];
      avms = Helpers.sortByDate(avms, 'updated');

      return avms[0].type === 'cma';
    },

    loans: function(home){
      return home && home.loans && home.loans.length ? home.loans : [];
    },

    ltvPercent: function(home){
      let homeValue = this.avm(home);
      let loans = this.currentLoansNoHelocs(home);
      let debt = this.outstandingPrincipalTotal({loans: loans});

      return (debt / homeValue) * 100;
    },

    marketRateOnSoldDate: function(home){
      var maxLoan = this.maxLoan(home);
      if(maxLoan)
        return maxLoan.rate || maxLoan.rateEstimated;

      return '';
    },

    maturityDate: function(home){
      var maxLoan = this.maxLoan(home);
      if(maxLoan)
        return maxLoan.maturity_date.nochange.date;

      return '';
    },

    maxLoan: function(home){
      var maxLoan = _.max(_.where(home.loans, {'status': this.loanStatus}), this.currentLoanWithMax);

      if(!_.isEmpty(maxLoan))
        return maxLoan;
      else
        return null;
    },

    monthlyPaymentEscrow: function(home){
      return 0;
    },

    monthlyPaymentInterestTotal: function(home,args){
      if(home.loans && home.loans.length)
        return _.sum(_.where(home.loans, {'status': this.loanStatus}),'payment.interest');
      return 0;
    },

    monthlyPaymentPrincipalTotal: function(home,args){
      if(home.loans && home.loans.length)
        return _.sum(_.where(home.loans, {'status': this.loanStatus}),'payment.principal');
      return 0;
    },

    monthlyPaymentTotal: function(home){
      if(home.loans && home.loans.length)
        return _.sum(_.where(home.loans, {'status': this.loanStatus}),'payment.total');
      return 0;
    },

    needsCMA: function(home){
      return Boolean(home.hasRequestedCMA && !this.hasUpToDateCMA(home));
    },

    nmlsLoanDescriptors: function(home){
      var descriptors = _.reduce(home.loans, function(statuses, loan){
          if(!loan.lo_nmls) return statuses;

          if(loan.refi === true)
            statuses.push('Refi');
          else
            statuses.push('Purchase');

          return statuses;
        }, []);

      if(descriptors.length)
        return descriptors.join(', ');

      return 'Unknown loan type';
    },

    originalLoanAmountTotal: function(home){
      if(home.loans && home.loans.length)
        return _.sum(_.where(home.loans, {'status': this.loanStatus}),'amount');
      return home.soldPrice;
    },

    originalLoanDate: function(home){
      var maxLoan = this.maxLoan(home);
      if(maxLoan)
        return maxLoan.date;

      return home.soldDate;
    },

    outstandingInterestTotal: function(home){
      if(home.loans && home.loans.length)
        return _.sum(_.where(home.loans, {'status': this.loanStatus}), 'progress.interest_left');
      return 0;
    },

    outstandingPrincipalTotal: function(home){
      if(home.loans && home.loans.length)
        return _.sum(_.where(home.loans, {'status': this.loanStatus}),'progress.principal_left');
      return 0;
    },

    ownershipPlan: function(home){
      return home.ownershipPlan || '';
    },

    ownershipPercentage: function(home){
      return (this.avm(home) - this.outstandingPrincipalTotal(home)) / this.avm(home) * 100;
    },

    periods: function(home){
      return this.termYears(home) * 12;
    },

    principalPaidTotal: function(home){
      if(home.loans && home.loans.length)
        return _.sum(_.filter(home.loans, {status: 'current'}), 'progress.principal_paid');
      return 0;
    },

    pmiTagline: function(home){
      let ltvPercent = this.ltvPercent(home);

      if(ltvPercent > 82)
        return 'Not yet.';

      if(ltvPercent <= 82 && ltvPercent >= 80)
        return 'Soon. Maybe now.';

      return 'Maybe now.';
    },

    pmiRateForFHA: function (loan) {
      if (!loan.date)
        return 0;

      if (moment(loan.date).isBetween('2013-06-30', '2015-01-01'))
        return .0135;

      if (moment(loan.date).isAfter('2014-12-31'))
        return .0085;

      return 0;
    },

    refiAPR: function(home, args){
      var maxLoan = this.maxLoan(home);
      if(maxLoan)
        return maxLoan.refiToday[args.rateType].APR;

      return 0;
    },

    refiFee: function(home, args){
      var maxLoan = this.maxLoan(home);
      if(maxLoan)
        return maxLoan.refiToday[args.rateType].fee;

      return 0;
    },

    refiRate: function(home, args){
      var maxLoan = this.maxLoan(home);
      if(maxLoan)
        return maxLoan.refiToday[args.rateType].rate;

      return 0;
    },

    refiPaymentSavingsMonthly: function(home, args){
      var pmiPaymentsForFHA = this.fhaPmiMonthlyTotal(home)

      return ((this.monthlyPaymentTotal(home) + pmiPaymentsForFHA) - this.refiPaymentMonthly(home, args));
    },

    refiPeriods: function(home, args){
      var maxLoan = this.maxLoan(home);
      if(maxLoan)
        return maxLoan.refiToday[args.rateType].periods;
    },

    remainingPeriods: function(home) {
      var periods = this.periods(home)
      var maxLoan = this.maxLoan(home)

      if (periods && maxLoan && maxLoan.date) {
        var curentMonth = moment().startOf('month')
        var loanStartingMonth = moment(maxLoan.date).startOf('month')
        var previousPeriods = curentMonth.diff(loanStartingMonth, 'months');
        return (periods - previousPeriods);
      }

      return 0;
    },

    refiSavingsTotal: function(home, args){
      var rate = args && args.rate ? args.rate : this.refiRate(home, args);
      var consolidationInterest = finance.calculateInterest(this.outstandingPrincipalTotal(home), this.refiPeriods(home, args), rate, new Date(), 0, false);

      var pmiPaymentsForFHA = (this.fhaPmiMonthlyTotal(home) * this.remainingPeriods(home));

      return -(consolidationInterest - (this.outstandingInterestTotal(home) + pmiPaymentsForFHA));
    },

    refiPaymentMonthly: function(home, args){
      var rate = args && args.rate ? args.rate : this.refiRate(home, args);
      return finance.calculatePayment(this.outstandingPrincipalTotal(home), this.refiPeriods(home, args), rate);
    },

    reverseMortgageEnabled: function(home){
      return Boolean(home.agent[0].office.brokerage.showReverseMortgage);
    },

    reverseMortgageQualified: function(home){
      var equityPercent = this.equityPercent(home);

      let homeownerAgeQualified = _.some(home.users, 'reverseMortgage');

      if (equityPercent >= 50 && homeownerAgeQualified) {
        return true;
      } else {
        return false;
      }
    },

    sellingAgent: function(home){
      try{
        if(home.sellingAgent.datasources.rets.status === 'success')
          return home.sellingAgent;
        return null;
      }catch(e){ return null; };
    },

    shouldRefi: function(home){
      return this.refiSavingsTotal(home) > 0;
    },

    show20YrRefi: function(home){
      if(this.monthsLeft(home) <= 22.5 * 12 && home.loans[0].refiToday.FIX20YR && !this.customTermsEnabled(home))
        return true;
      else
        return false;
    },

    show25YrRefi: function(home){
      if(this.monthsLeft(home) > 22.5 * 12 && home.loans[0].refiToday.FIX25YR && !this.customTermsEnabled(home))
        return true;
      else
        return false;
    },

    showCustomRefi: function(home){
      if(this.yearsLeft(home) < 18 || this.yearsLeft(home) > 27 || !this.customTermsEnabled(home))
        return false;
      else
        return true;
    },

    showCashoutRefi: function(home){
      if(!this.currentLoansNoHelocs(home).length)
        return true;

      home = _.cloneDeep(home);
      home.loans = this.eligibleRefiLoans(home);

      return this._hasEligibleRefiLoans(home)
        && this.cashoutVal(home) > 5000;
    },

    showAvmDiffWarning: function(home, diffPercent = 10){
      return !this.showNonDisclosureStateWarning(home)
        && !this.latestAvmIsCma(home)
        && home.metadata.avmDiffPercent > diffPercent;
    },

    showBuyingPowerSection: function(home){
      if(!this.currentLoansNoHelocs(home).length)
        return true;

      return this.ltvPercent(home) < 75;
    },

    showNonDisclosureStateWarning: function(home){
      const nonDisclosureStates = ['AK','ID','KS','LA','MS','MO','MT','NM','ND','TX','UT','WY'];

      if(this.cma(home)) return false;

      let state = home.address.state
      if(!state) return false;

      state = state.toUpperCase();

      return nonDisclosureStates.includes(state);
    },

    showRefiForFHA: function(home){
      var maxLoan = this.maxLoan(home)
      return this.isFHA(home) && this.pmiRateForFHA(maxLoan);
    },

    showReverseMortgageSection: function(home){
      return this.reverseMortgageEnabled(home) && this.reverseMortgageQualified(home);
    },

    showPmiSection: function(home){
      var hasQualifyingLTV = moment().isBefore(home.metadata.ltvAutomatedDates['78']);

      if (!hasQualifyingLTV) {
        return false;
      }

      var hasConventionalPMI = this.conventionalPmiMonthlyTotal(home);

      if (hasConventionalPMI) {
        return true;
      }

      const agent = _.get(home, 'agent[0]', {})

      // HE-1770
      if (agent.type !== "realestate") {
        return false
      }

      return this.hasLoanQualifyingForEstimatedPmi(home);
    },

    hasLoanQualifyingForEstimatedPmi: function(home) {
      var currentLoans = this.currentLoans(home);
      var singleLoan = currentLoans.length === 1 && currentLoans[0];

      if(!home.soldPrice || !home.soldDate || !singleLoan)
        return false;

      if(moment(singleLoan.date).diff(moment(home.soldDate), 'months', true) > 3)
        return false;

      return !singleLoan.pmi_monthly &&
        singleLoan.loan_type !== 'HELOC' &&
        singleLoan.finance_type !== 'FHA' &&
        !singleLoan.adjustable &&
        // Only show if we have available estimate data
        Boolean(this.estimatedPmiMonthlyTotal(home));
    },

    termHistory: function(home){
      if(this.term(home) === 15)
        return 'FIX15YR';

      return 'FIX30YR';
    },

    termYears: function(home){
      var maxLoan = this.maxLoan(home);
      if(maxLoan)
        return maxLoan.termYears || maxLoan.termYearsEstimated;

      return 30;
    },

    unverifiedLoans: function(home){
      var THIS = this;
      return _.filter(home.loans, function(loan){
        return loan.status === THIS.loanStatus && (!loan.termYears || !loan.rate);
      });
    },

    totalPaid: function(home){
      return home.soldPrice - this.outstandingPrincipalTotal(home);
    },

    monthsLeft: function(home){
      var maxLoan = this.maxLoan(home);
      if(maxLoan)
        return maxLoan.progress.payments_left;

      return 0;
    },

    yearsLeft: function(home){
      var maxLoan = this.maxLoan(home);
      if(maxLoan)
        return Math.round(maxLoan.progress.payments_left / 12);

      return 0;
    }

  }

};

if(typeof module !== 'undefined' && module.exports){
  module.exports = GlobalDictionary;
}else if(typeof window !== 'undefined'){
  window.GlobalDictionary = GlobalDictionary;
}
